import { useState, type FormEvent } from "react";
import type { DeliverableDto, PartnerDto, SalespageDeliveryDto } from "../../shared/dto";
import { InfoBlock } from "../InfoBlock";
import { workflowPillClass } from "../workflow-pill";
import { GateDocCard } from "./DocumentCards";
import { StrategyWaitingState } from "./StrategySection";
import {
  ActionError,
  FocusedSalesPage,
  InlineLockIcon,
  MaterialFeedbackSummary,
  ReviewLoopIntro,
  focusedState,
  staffReturnedReason,
  type DeliverySectionContentProps
} from "./shared";

export function DocumentPage(
  props: DeliverySectionContentProps & { kind: "emails" | "webinar" }
) {
  const item = props.deliverables.find((candidate) => candidate.kind === props.kind);
  return (
    <FocusedSalesPage page={props.kind} state={focusedState(props, props.kind)} t={props.t}>
      {item?.status === "review" && props.membershipRole === "owner" && (
        <>
          <ReviewLoopIntro material={props.kind} placement="step" t={props.t} />
          <MaterialFeedbackSummary
            deliverableId={item.id}
            version={item.version}
            refreshVersion={props.feedbackRevision}
            onVersionAdvanced={props.onReloadData}
            t={props.t}
          />
        </>
      )}
      <div className="focused-sales-content">
        {item ? (
          <GateDocCard
            kind={props.kind}
            deliverable={item}
            canAct={props.membershipRole === "owner"}
            canApprove
            busyAction={props.busyAction}
            csrfToken={props.csrfToken}
            staffReturnedReason={staffReturnedReason(props.resources, props.kind)}
            t={props.t}
            onApprove={() => props.onGateApprove?.(item.id)}
            onFeedback={(payload) => Promise.resolve(props.onGateFeedback?.(item.id, payload) ?? false)}
          />
        ) : (
          <StrategyWaitingState
            material={props.kind}
            profilePhotoUrl={props.profilePhotoUrl}
            titleKey={`sales.preparingWait.${props.kind}.title`}
            copyKey={`sales.preparingWait.${props.kind}.copy`}
            etaKey={`sales.preparingWait.${props.kind}.eta`}
            t={props.t}
          />
        )}
      </div>
      <ActionError t={props.t} issue={props.actionError} />
    </FocusedSalesPage>
  );
}

export function PublishPage(props: DeliverySectionContentProps) {
  return (
    <section className="m5b-view focused-sales-page" data-focused-sales-page="publish" aria-labelledby="publish-title">
      <header className="m5b-heading">
        <p className="eyebrow blue-eyebrow">{props.t("sales.pages.publish.eyebrow")}</p>
        <h1 id="publish-title">{props.t("rail.steps.salespage_publish")}</h1>
        <p className="lead">{props.t("sales.pages.publish.copy")}</p>
      </header>
      <PublishSalespageStep
        deliverable={props.deliverables.find((item) => item.kind === "salespage")}
        state={props.salespageDelivery}
        csrfToken={props.csrfToken}
        partner={props.partner}
        isOwner={props.membershipRole === "owner"}
        t={props.t}
        onReloadData={props.onReloadData}
      />
    </section>
  );
}

// Sales page delivery decision (D10 + D11), pure and exported for tests:
// locked (Stripe milestone missing), update (a newer version than the one on
// Netlify), ready (menu open), hidden (no state from the server).
export function salespageDeliveryPhase(
  state: SalespageDeliveryDto | undefined | null,
  version: number
): "hidden" | "locked" | "update" | "ready" {
  if (!state) return "hidden";
  if (state.locked) return "locked";
  if (state.netlify && version > state.netlify.publishedVersion) return "update";
  return "ready";
}

export type PublishSalespagePhase =
  "waiting_page" | "locked_page" | "locked_stripe" | "ready" | "update" | "published";

export function publishSalespagePhase(
  deliverable: Pick<DeliverableDto, "status" | "version"> | undefined | null,
  state: SalespageDeliveryDto | undefined | null
): PublishSalespagePhase {
  if (!deliverable || !state) return "waiting_page";
  if (deliverable.status !== "approved") return "locked_page";
  if (state.locked) return "locked_stripe";
  if (!state.netlify) return "ready";
  if (deliverable.version > state.netlify.publishedVersion) return "update";
  return "published";
}

function PublishSalespageStep(props: {
  deliverable?: DeliverableDto;
  state?: SalespageDeliveryDto | null;
  csrfToken?: string;
  partner: PartnerDto | null;
  isOwner: boolean;
  t: (key: string) => string;
  onReloadData?: () => Promise<void>;
}) {
  const phase = publishSalespagePhase(props.deliverable, props.state);
  const unlocked = phase === "ready" || phase === "update" || phase === "published";
  const pageState = phase === "waiting_page" ? "preparing" : phase === "locked_page" ? "awaiting_approval" : "done";
  const stripeState = phase === "locked_stripe" ? "awaiting_client" : unlocked ? "done" : "locked";
  const deliveryState = phase === "published" ? "done" : unlocked ? "awaiting_client" : "locked";
  const timeline = [
    { key: "page", state: pageState },
    { key: "stripe", state: stripeState },
    { key: "delivery", state: deliveryState }
  ];
  return (
    <article
      className={`salespage-publish-step phase-${phase}`}
      data-sales-step="publish"
      data-locked-preview={!unlocked ? "publish" : undefined}
    >
      <div className="salespage-publish-heading">
        <div>
          <p className="eyebrow blue-eyebrow">{props.t("sales.publish.eyebrow")}</p>
          <h2>{props.t("sales.publish.title")}</h2>
        </div>
        <span className={`status-pill ${phase === "published" ? "pill-done" : unlocked ? "pill-client" : "pill-locked"}`}>
          {props.t(`sales.publish.${phase}`)}
        </span>
      </div>
      <ol className="publish-timeline" aria-label={props.t("sales.publish.timeline.title")}>
        {timeline.map((item) => (
          <li key={item.key} data-publish-checkpoint={item.key}>
            <strong>{props.t(`sales.publish.timeline.${item.key}`)}</strong>
            <span className={`status-pill ${workflowPillClass(item.state)}`}>
              {props.t(`sales.publish.timeline.${item.state}`)}
            </span>
          </li>
        ))}
      </ol>
      {!unlocked && (
        <div className="salespage-publish-lock" role="status">
          <InlineLockIcon />
          <div className="salespage-publish-lock-copy">
            <p>{props.t(`sales.publish.${phase === "waiting_page" ? "waitingPage" : phase === "locked_page" ? "lockedPage" : "lockedStripe"}`)}</p>
            <small>{props.t("sales.publish.timeline.next")}</small>
          </div>
        </div>
      )}
      {unlocked && (
        <>
          {props.isOwner && props.deliverable && props.state && (
            <SalespageDeliveryMenu
              deliverable={props.deliverable}
              state={props.state}
              csrfToken={props.csrfToken}
              partner={props.partner}
              t={props.t}
              onReloadData={props.onReloadData}
              showTitle={false}
            />
          )}
        </>
      )}
    </article>
  );
}

// Two delivery paths: ZIP download, or Netlify. Custom-subdomain DNS belongs
// inside Netlify because it points to the published Netlify site.
// Until Stripe is connected the whole menu is one truthful lock note (D10).
type NetlifyPublishError = "auth" | "token" | "deploy" | "stripe" | "artifact" | "pending";

export function netlifyPublishErrorKind(error: string | undefined): NetlifyPublishError {
  if (error === "netlify_auth_failed") return "auth";
  if (error === "netlify_token_required") return "token";
  // F82 gate code, plus the historic stripe_required from a cached worker.
  if (error === "order_links_required" || error === "stripe_required") return "stripe";
  if (error === "artifact_unavailable") return "artifact";
  if (error === "netlify_deploy_pending") return "pending";
  return "deploy";
}

export function SalespageDeliveryMenu(props: {
  deliverable: DeliverableDto;
  state: SalespageDeliveryDto;
  csrfToken?: string;
  partner: PartnerDto | null;
  t: (key: string) => string;
  onReloadData?: () => Promise<void>;
  showTitle?: boolean;
}) {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<NetlifyPublishError | null>(null);
  const [publishedUrl, setPublishedUrl] = useState<string | null>(null);
  const [customDomain, setCustomDomain] = useState("");
  const [configuredDomain, setConfiguredDomain] = useState<string | null>(null);
  const [domainState, setDomainState] = useState<"idle" | "saving" | "done" | "auth" | "error">("idle");
  const phase = salespageDeliveryPhase(props.state, props.deliverable.version);

  if (phase === "locked") {
    return (
      <div className="salespage-lock" role="status">
        <p className="inline-lock-copy"><InlineLockIcon />{props.t("sales.delivery.lockedCopy")}</p>
      </div>
    );
  }

  const netlify = props.state.netlify;
  // A stored token publishes a new version without re-pasting; the server
  // answers netlify_token_required when it needs one after all.
  async function publish() {
    if (!props.csrfToken) return;
    setBusy(true);
    setError(null);
    try {
      const trimmed = token.trim();
      const response = await fetch("/api/delivery/salespage/netlify", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Launchpad-CSRF": props.csrfToken,
          "Idempotency-Key": crypto.randomUUID()
        },
        body: JSON.stringify(trimmed ? { token: trimmed } : {})
      });
      const body = await response.json() as { ok?: boolean; error?: string; siteUrl?: string };
      if (!response.ok || body.ok !== true) {
        setError(netlifyPublishErrorKind(body.error));
        return;
      }
      setToken("");
      setPublishedUrl(body.siteUrl ?? null);
      await props.onReloadData?.();
    } catch {
      setError("deploy");
    } finally {
      setBusy(false);
    }
  }

  async function configureDomain() {
    if (!props.csrfToken || !customDomain.trim()) return;
    setDomainState("saving");
    try {
      const response = await fetch("/api/delivery/salespage/netlify-domain", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Launchpad-CSRF": props.csrfToken,
          "Idempotency-Key": crypto.randomUUID()
        },
        body: JSON.stringify({ domain: customDomain.trim() })
      });
      const body = await response.json() as { ok?: boolean; error?: string; domain?: string };
      if (!response.ok || body.ok !== true || !body.domain) {
        setDomainState(body.error === "netlify_auth_failed" ? "auth" : "error");
        return;
      }
      setConfiguredDomain(body.domain);
      setCustomDomain(body.domain);
      setDomainState("done");
    } catch {
      setDomainState("error");
    }
  }

  const needsToken = !netlify || error === "token" || error === "auth";
  return (
    <div className="salespage-delivery">
      {props.showTitle !== false && <h3>{props.t("sales.delivery.title")}</h3>}
      <div className="salespage-delivery-options">
        <div className="salespage-delivery-option" data-delivery-option="zip">
          <a className="secondary-button" href="/api/delivery/salespage/download" download>
            {props.t("sales.delivery.download")}
          </a>
          <small>{props.t("sales.delivery.downloadNote")}</small>
        </div>

        <div className="salespage-delivery-option" data-delivery-option="netlify">
          {netlify ? (
            <>
              <a className="quiet-link salespage-live-url break-text" href={netlify.siteUrl} target="_blank" rel="noreferrer">
                {netlify.siteUrl.replace(/^https:\/\//, "")}
              </a>
              <small>{props.t("sales.delivery.netlifyYours")}</small>
              {phase === "update" && (
                <button className="secondary-button" type="button" disabled={busy} onClick={() => void publish()}>
                  {busy ? props.t("sales.delivery.netlifyPublishing") : props.t("sales.delivery.netlifyUpdate")}
                </button>
              )}
              {publishedUrl && !busy && phase !== "update" && (
                <p className="message success-message" role="status">{props.t("sales.delivery.netlifyDone")}</p>
              )}
            </>
          ) : (
            <>
              <strong className="salespage-netlify-title">{props.t("sales.delivery.netlify")}</strong>
              <small>{props.t("sales.delivery.netlifyNote")}</small>
            </>
          )}
          <div className="salespage-netlify-panel">
            {/* 🔴 F137: a client whose site is already published must not be
                walked through creating the account they own and pasting a
                token we hold, ending at a button that is not on the page any
                more (step 4). It reads as work still owed on a phase that says
                "hotovo" beside it. The condition is the SAME one the publish
                button below uses, so the instructions and the control they
                describe can never appear apart: back after an expired token
                (`needsToken`), the walkthrough is relevant again and returns
                with it. */}
            {(!netlify || needsToken) && (
              <>
                <p>{props.t("sales.delivery.netlifyExplain")}</p>
                <ol className="salespage-netlify-steps">
                  <li>
                    <a href="https://app.netlify.com/signup" target="_blank" rel="noreferrer">
                      {props.t("sales.delivery.netlifyAccountLink")}
                    </a>
                    {" "}{props.t("sales.delivery.netlifyAccountStep")}
                  </li>
                  <li>
                    <a href="https://app.netlify.com/user/applications#personal-access-tokens" target="_blank" rel="noreferrer">
                      {props.t("sales.delivery.netlifyTokenLink")}
                    </a>
                    {" "}{props.t("sales.delivery.netlifyTokenStep")}
                  </li>
                  <li>{props.t("sales.delivery.netlifyPasteStep")}</li>
                  <li>{props.t("sales.delivery.netlifyPublishStep")}</li>
                </ol>
              </>
            )}
            {needsToken && (
              <input
                type="password"
                value={token}
                onChange={(event) => { setToken(event.target.value); setError(null); }}
                placeholder={props.t("sales.delivery.netlifyTokenPlaceholder")}
                autoComplete="off"
                spellCheck={false}
              />
            )}
            {(!netlify || needsToken) && (
              <button
                className="primary-button"
                type="button"
                disabled={busy || (needsToken && !token.trim())}
                onClick={() => void publish()}
              >
                {busy ? props.t("sales.delivery.netlifyPublishing") : props.t("sales.delivery.netlifyPublish")}
              </button>
            )}
            {error === "auth" && <p className="message error-message" role="alert">{props.t("sales.delivery.netlifyAuthError")}</p>}
            {error === "token" && <p className="message error-message" role="alert">{props.t("sales.delivery.netlifyTokenError")}</p>}
            {error === "deploy" && <p className="message error-message" role="alert">{props.t("sales.delivery.netlifyDeployError")}</p>}
            {error === "artifact" && <p className="message error-message" role="alert">{props.t("sales.delivery.netlifyArtifactError")}</p>}
            {error === "pending" && <p className="message error-message" role="alert">{props.t("sales.delivery.netlifyPendingError")}</p>}
            {error === "stripe" && <p className="message error-message" role="alert">{props.t("sales.delivery.lockedCopy")}</p>}
            <InfoBlock
              name="dns"
              className="salespage-dns-guide"
              eyebrow={props.t("sales.delivery.dnsEyebrow")}
              title={props.t("sales.delivery.dnsTitle")}
              body={<p>{props.t("sales.delivery.dnsExplain")}</p>}
              meta={domainState === "done"
                ? <span className="status-pill pill-done">{props.t("sales.delivery.dnsConfigured")}</span>
                : undefined}
            >
              {netlify ? (
                <>
                  <label className="salespage-domain-field">
                    <span>{props.t("sales.delivery.dnsDomainLabel")}</span>
                    <input
                      name="netlify-custom-domain"
                      type="text"
                      inputMode="url"
                      value={customDomain}
                      placeholder={props.t("sales.delivery.dnsDomainPlaceholder")}
                      onChange={(event) => {
                        setCustomDomain(event.target.value);
                        setDomainState("idle");
                      }}
                    />
                  </label>
                  <button
                    className="secondary-button"
                    type="button"
                    disabled={!props.csrfToken || domainState === "saving" || !customDomain.trim()}
                    onClick={() => void configureDomain()}
                  >
                    {props.t(domainState === "saving" ? "sales.delivery.dnsConfiguring" : "sales.delivery.dnsConfigure")}
                  </button>
                  {domainState === "auth" && <p className="message error-message" role="alert">{props.t("sales.delivery.dnsAuthError")}</p>}
                  {domainState === "error" && <p className="message error-message" role="alert">{props.t("sales.delivery.dnsConfigureError")}</p>}
                  <ol className="salespage-dns-steps">
                    {(["provider", "record", "wait"] as const).map((key) => (
                      <li key={key}>{props.t(`sales.delivery.dnsStep.${key}`)}</li>
                    ))}
                  </ol>
                  <dl className="salespage-dns-record">
                    <div><dt>{props.t("sales.delivery.dnsType")}</dt><dd>{props.t("sales.delivery.dnsRecordType")}</dd></div>
                    <div>
                      <dt>{props.t("sales.delivery.dnsName")}</dt>
                      <dd>{configuredDomain ?? props.t("sales.delivery.dnsHost")}</dd>
                    </div>
                    <div><dt>{props.t("sales.delivery.dnsValue")}</dt><dd>{netlify.dnsTarget}</dd></div>
                    <div><dt>{props.t("sales.delivery.dnsTtl")}</dt><dd>3600</dd></div>
                  </dl>
                </>
              ) : (
                <p className="salespage-dns-pending">{props.t("sales.delivery.dnsPending")}</p>
              )}
              {/* F139: a client with NO partner cannot arrange a call with
                  one, so the offer is not drawn at all. The partner-without-a
                  -booking-link branch keeps its sentence: that partner exists
                  and can be written to from the right column. */}
              {props.partner?.calendlyUrl ? (
                <a className="quiet-link" href={props.partner.calendlyUrl} target="_blank" rel="noreferrer">
                  {props.t("sales.delivery.dnsPartnerCall")}
                </a>
              ) : props.partner ? (
                <p>{props.t("sales.delivery.dnsPartnerCall")}</p>
              ) : null}
            </InfoBlock>
          </div>
        </div>
      </div>
    </div>
  );
}
